import {Controller} from "@hotwired/stimulus"
import {useZoomListener} from "./mixins/use_zoom_listener.js";
import {useResizeListener} from "./mixins/use_resize_listener.js";

/**
 * A Stimulus controller that automatically resizes input elements
 * based on their content width. It uses a hidden mirror element
 * to measure the rendered text width and adjusts the input's min-width accordingly.
 *
 * This ensures the input grows horizontally as the user types, without wrapping.
 *
 * Usage:
 *   = f.input :name, input_html: {data: { controller: 'text-input'}}
 *
 * It responds to input events, window resize, and zoom changes to maintain proper sizing.
 * Requires the useZoomListener and useResizeListener mixins.
 */

export default class extends Controller {
  mirrorElement = null;
  inputComputedStyles = null;

  connect() {
    super.connect();
    useZoomListener(this);
    useResizeListener(this);
    this._resize();
    this.resizeHandler = this._onInput.bind(this);
    this.element.addEventListener('input', this.resizeHandler);
  }

  disconnect() {
    super.disconnect();
    this.element.removeEventListener('input', this.resizeHandler);
    this.mirrorElement?.remove();
  }

  _onInput() {
    this._resize();
  }

  _resize() {
    this._createMirrorElement();
    this._copyStyles();
    this._copyText();
    this._setInputWidth();
  }

  _createMirrorElement() {
    this.mirrorElement = document.createElement('span');
    this.mirrorElement.classList.add('fixed', 'invisible', 'whitespace-nowrap');
    document.body.appendChild(this.mirrorElement);
  }

  _copyStyles() {
    this.inputComputedStyles = getComputedStyle(this.element);
    const cssPropsToCopy = [
      'fontSize','fontFamily','fontWeight','fontStyle','letterSpacing',
      'textTransform','paddingLeft','paddingRight','borderLeftWidth','borderRightWidth'
    ];
    cssPropsToCopy.forEach(prop => {
      this.mirrorElement.style[prop] = this.inputComputedStyles[prop];
    });
  }

  _copyText() {
    this.mirrorElement.textContent = this.element.value || this.element.placeholder || '';
  }

  _setInputWidth() {
    const prevMinWidth = parseInt(this.inputComputedStyles.minWidth);
    const mirrorElementWidth = this.mirrorElement.offsetWidth;
    if (mirrorElementWidth > prevMinWidth) {
      this.element.style.minWidth = `${mirrorElementWidth + 2}px`;
    }
    this.mirrorElement.remove();
  }

  onResizeChange() {
    this._resize();
  }

  onZoomChange() {
    this._resize();
  }
}